import { useCallback, useEffect, useRef, useState } from 'react';

const getAverageImageColor = (src, onColorChange) => {
  const image = new Image();
  image.src = src;

  image.onload = () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (!context) {
      return;
    }

    const size = 18;
    canvas.width = size;
    canvas.height = size;
    context.drawImage(image, 0, 0, size, size);

    const { data } = context.getImageData(0, 0, size, size);
    let red = 0;
    let green = 0;
    let blue = 0;
    let samples = 0;

    for (let index = 0; index < data.length; index += 16) {
      red += data[index];
      green += data[index + 1];
      blue += data[index + 2];
      samples += 1;
    }

    onColorChange(`${Math.round(red / samples)}, ${Math.round(green / samples)}, ${Math.round(blue / samples)}`);
  };
};

export const useGallery = (images, { keyboardNavigation = true } = {}) => {
  const activeThumbRef = useRef(null);
  const thumbsTrackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [glowColor, setGlowColor] = useState('92, 98, 236');

  const activeImage = images[activeIndex];
  const isFirstSlide = activeIndex === 0;
  const isLastSlide = activeIndex === images.length - 1;

  const openModal = useCallback((index) => {
    setActiveIndex(index);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const showPrevious = useCallback(() => {
    setActiveIndex((currentIndex) => Math.max(0, currentIndex - 1));
  }, []);

  const showNext = useCallback(() => {
    setActiveIndex((currentIndex) => Math.min(images.length - 1, currentIndex + 1));
  }, [images.length]);

  useEffect(() => {
    if (!activeImage) {
      return;
    }

    getAverageImageColor(activeImage.preview || activeImage.src, setGlowColor);
  }, [activeImage]);

  useEffect(() => {
    if (!isOpen || !keyboardNavigation) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.repeat) return;

      if (event.key === 'Escape') {
        closeModal();
      }

      if (event.key === 'ArrowLeft') {
        showPrevious();
      }

      if (event.key === 'ArrowRight') {
        showNext();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeModal, isOpen, keyboardNavigation, showNext, showPrevious]);

  useEffect(() => {
    if (!isOpen || !thumbsTrackRef.current) {
      return;
    }

    if (activeIndex === 0) {
      thumbsTrackRef.current.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
      return;
    }

    if (activeIndex === images.length - 1) {
      thumbsTrackRef.current.scrollTo({
        left: thumbsTrackRef.current.scrollWidth,
        behavior: 'smooth',
      });
      return;
    }

    if (activeThumbRef.current) {
      activeThumbRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeIndex, images.length, isOpen]);

  return {
    activeImage,
    activeIndex,
    activeThumbRef,
    thumbsTrackRef,
    closeModal,
    glowColor,
    isFirstSlide,
    isLastSlide,
    isOpen,
    openModal,
    setActiveIndex,
    showNext,
    showPrevious,
  };
};
